import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthConfig } from "./config";
import { signIn as cognitoSignIn, registerViaBackend, tryRestoreSession, signOutFromCognito } from "./cognito";
import { setAuthToken, setOnUnauthorized, triggerUnauthorized } from "./token";

type AuthContextValue = {
  idToken: string | null;
  configReady: boolean;
  authReady: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signOut: () => void;
  register: (email: string, password: string, discountCode: string) => Promise<void>;
  error: string | null;
  clearError: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [configReady, setConfigReady] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    getAuthConfig().then(() => setConfigReady(true));
  }, []);

  useEffect(() => {
    setOnUnauthorized(() => {
      setIdToken(null);
      setAuthToken(null);
      navigate("/login", { replace: true });
    });
    return () => setOnUnauthorized(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!configReady) return;
    let cancelled = false;
    getAuthConfig().then((config) => {
      if (!config || cancelled) return;
      tryRestoreSession(config).then((token) => {
        if (cancelled || token === null) return;
        setAuthToken(token);
        setIdToken(token);
        setAuthReady(true);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [configReady]);

  const signIn = useCallback(async (email: string, password: string, rememberMe: boolean = true) => {
    setError(null);
    const config = await getAuthConfig();
    if (!config) {
      setError("Auth is not configured. Cannot sign in.");
      return;
    }
    try {
      const token = await cognitoSignIn(config, email, password, rememberMe);
      setIdToken(token);
      setAuthToken(token);
      setAuthReady(true);
      navigate("/app", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed.");
    }
  }, [navigate]);

  const signOut = useCallback(() => {
    setIdToken(null);
    setAuthToken(null);
    setAuthReady(false);
    getAuthConfig().then((config) => {
      if (config) signOutFromCognito(config);
    });
    triggerUnauthorized();
  }, []);

  const register = useCallback(async (email: string, password: string, discountCode: string) => {
    setError(null);
    try {
      await registerViaBackend(email, password, discountCode);
      await signIn(email, password);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Registration failed.";
      setError(msg);
    }
  }, [navigate, signIn]);

  const value: AuthContextValue = {
    idToken,
    configReady,
    authReady,
    signIn,
    signOut,
    register,
    error,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
